import { useState } from "react";
import { Image as ImageIcon, FileText, Table, Presentation, File as FileIcon } from "lucide-react";
import { ImageViewer } from "../ImageViewer/ImageViewer";
import styles from "./UserChatMessage.module.css";
import { SessionAttachment } from "../../types/uploads";

interface Props {
    message: string;
    attachments?: SessionAttachment[];
}

export const UserChatMessage = ({ message, attachments }: Props) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<{ src: string; name: string } | null>(null);

    const isImageFile = (filename: string, contentType?: string) => {
        if (contentType && contentType.startsWith("image/")) {
            return true;
        }
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.bmp', '.heic', '.tiff', '.gif', '.webp'];
        const extensionIndex = filename.lastIndexOf('.');
        const extension = extensionIndex >= 0 ? filename.substring(extensionIndex).toLowerCase() : '';
        return imageExtensions.includes(extension);
    };

    const handleImageClick = (src: string, name: string) => {
        setSelectedImage({ src, name });
        setViewerOpen(true);
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setSelectedImage(null);
    };

    const getFileTypeInfo = (filename: string): { icon: JSX.Element; label: string; color: string } => {
        const extension = filename.substring(filename.lastIndexOf('.')).toLowerCase();

        switch (extension) {
            case '.pdf':
                return {
                    icon: <FileText size={32} />,
                    label: 'PDF',
                    color: '#DC2626' // Red
                };
            case '.xlsx':
            case '.xls':
            case '.csv':
                return {
                    icon: <Table size={32} />,
                    label: extension === '.csv' ? 'CSV' : 'Excel',
                    color: '#16A34A' // Green
                };
            case '.docx':
            case '.doc':
                return {
                    icon: <FileText size={32} />,
                    label: 'Word',
                    color: '#2563EB' // Blue
                };
            case '.pptx':
            case '.ppt':
                return {
                    icon: <Presentation size={32} />,
                    label: 'PowerPoint',
                    color: '#EA580C' // Orange
                };
            case '.txt':
            case '.md':
                return {
                    icon: <FileText size={32} />,
                    label: 'Text',
                    color: '#6B7280' // Grey
                };
            case '.json':
                return {
                    icon: <FileText size={32} />,
                    label: 'JSON',
                    color: '#6B7280' // Grey
                };
            default:
                return {
                    icon: <FileIcon size={32} />,
                    label: 'File',
                    color: '#6B7280' // Grey
                };
        }
    };

    const attachmentList = attachments ?? [];

    return (
        <>
            <div className={styles.container}>
                {attachmentList.length > 0 && (
                    <div className={styles.attachments}>
                        {attachmentList.map((attachment, index) => {
                            const attachmentKey = attachment.id ?? `${attachment.name}-${index}`;
                            const src = attachment.dataUrl ?? attachment.sasUrl ?? attachment.blobUrl;
                            const isImage = isImageFile(attachment.name, attachment.contentType);

                            if (isImage) {
                                return (
                                    <div
                                        key={attachmentKey}
                                        className={styles.imageThumbnail}
                                        onClick={src ? () => handleImageClick(src, attachment.name) : undefined}
                                        style={{ cursor: src ? 'pointer' : 'default' }}
                                    >
                                        {src ? (
                                            <img
                                                src={src}
                                                alt={attachment.name}
                                                className={styles.thumbnailImage}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const fallback = e.currentTarget.nextElementSibling;
                                                    if (fallback) {
                                                        (fallback as HTMLElement).style.display = 'flex';
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <div className={styles.imagePlaceholder} style={{ display: src ? 'none' : 'flex' }}>
                                            <ImageIcon size={24} />
                                        </div>
                                        <span className={styles.imageFilename}>{attachment.name}</span>
                                    </div>
                                );
                            }

                            return (
                                <div key={attachmentKey} className={styles.documentThumbnail}>
                                    <div
                                        className={styles.documentIconContainer}
                                        style={{ color: getFileTypeInfo(attachment.name).color }}
                                    >
                                        {getFileTypeInfo(attachment.name).icon}
                                    </div>
                                    <div className={styles.documentMetadata}>
                                        <span className={styles.documentFileName} title={attachment.name}>
                                            {attachment.name}
                                        </span>
                                        <span className={styles.documentFileType}>
                                            {getFileTypeInfo(attachment.name).label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={styles.message}>{message}</div>
            </div>
            {selectedImage && (
                <ImageViewer
                    src={selectedImage.src}
                    alt={selectedImage.name}
                    isOpen={viewerOpen}
                    onClose={handleCloseViewer}
                />
            )}
        </>
    );
};
